import { Injectable } from '@angular/core';
import { Amplify } from 'aws-amplify';
import appConfig from '../../app-config';
import { AuthService } from './auth.service';
import { Hub } from 'aws-amplify/utils';

type cognitoHostedStates = 'cognitoHostedUI_failure' | 'cognitoHostedUI';
type authSignInStates = 'signInWithRedirect' | 'signedIn' | 'signInWithRedirect_failure';
type customStates = 'customState_failure' | 'customOAuthState' | 'parsingCallbackUrl';

interface HubPayload {
  event: cognitoHostedStates | authSignInStates | customStates;
  data?: any;
  message?: string;
}

interface HubCapsule {
  channel: string;
  payload: HubPayload;
  source: string;
  patternInfo?: string[];
}

export interface AppConfigServiceInterface {
  loadAppConfig(authService: AuthService): Promise<boolean>;
  getConfig(): any;
  /**
   * Listens to hub events and execute different steps bases on events
   * @param authService To manage the login and error state
   */
  listenAuthState(authService: AuthService): any;
}

@Injectable({
  providedIn: 'root',
})
export class AppConfigService implements AppConfigServiceInterface {
  private appConfig: any;

  public loadAppConfig(authService: AuthService) {
    this.appConfig = appConfig;

    Amplify.configure(this.getAmplifyConfig().authConfig);
    Hub.listen('auth', this.listenAuthState(authService) as any);

    return authService.validateLogin();
  }

  private getAmplifyConfig(): any {
    return {
      authConfig: {
        Auth: {
          Cognito: {
            region: 'us-east-1',
            userPoolId: this.appConfig.auth.user_pool_id,
            userPoolClientId: this.appConfig.auth.client_id,
            identityPoolId: this.appConfig.auth.identity_pool_id,
            localStorage: {
              domain: this.appConfig.auth.domain,
              path: '/',
              expires: 7,
              sameSite: 'strict',
              secure: true,
            },
            loginWith: {
              oauth: {
                scopes: this.appConfig.auth.scope,
                responseType: this.appConfig.auth.response_type,
                redirectSignIn: this.appConfig.auth.redirect_uri,
                redirectSignOut: this.appConfig.auth.redirect_uri,
                domain: this.appConfig.auth.hostname,
              },
            },
          },
        },
        API: {
          GraphQL: {
            endpoint: this.appConfig.api.aws_appsync_graphqlEndpoint,
            region: this.appConfig.api.aws_appsync_region,
            defaultAuthMode: this.appConfig.api.aws_appsync_authenticationType,
          },
        },
      },
    } as const;
  }

  public listenAuthState(authService: AuthService) {
    return (data: HubCapsule): void => {
      if (data.channel !== 'auth') return;

      const { event, message } = data.payload;

      if (!message) return;
      switch (event) {
        case 'signInWithRedirect_failure': {
          authService.errorState = {
            message,
          };
          break;
        }

        case 'signInWithRedirect': {
          authService.loginState = true;
          authService.fetchUserDetails();
          break;
        }

        case 'parsingCallbackUrl': {
          this.refreshUserState(authService);
        }

        // no default
      }
    };
  }

  async refreshUserState(authService: AuthService) {
    const activeSession = await authService.validateLogin();
    if (activeSession) {
      authService.loginState = true;
      authService.fetchUserDetails();
    }
  }

  public getConfig() {
    return this.appConfig;
  }
}
